<div class="webhook-edit-page">
  @if (facade.isLoading()) {
    <zag-loading-state [message]="'common.loading' | translate" />
  } @else if (facade.error()) {
    <zag-error-state [message]="facade.error()!" [backLink]="'/settings/webhooks'" [backText]="'common.back' | translate" />
  } @else {
    @if (canManage()) {
      <ng-template zagPageActions>
        <button zagButton type="button" [disabled]="saveDisabled()" (click)="facade.onSave()">
          {{ facade.isSaving() ? ('common.saving' | translate) : ('common.save' | translate) }}
        </button>
      </ng-template>
    }

    <zag-entity-detail-layout>
      <!-- Sidebar -->
      <div slot="sidebar">
        <zag-profile-summary-card
          [avatarName]="facade.webhookName()"
          avatarIcon="webhook"
          [subtitle]="facade.webhookSubtitle()"
          [stats]="facade.profileStats()"
          [quickActions]="facade.quickActions()"
          [dangerActions]="facade.dangerActions()"
          [quickActionsTitle]="'common.quickActions' | translate"
          [dangerZoneTitle]="'common.dangerZone' | translate"
          (quickActionClick)="onQuickAction($event)"
          (dangerActionClick)="onDangerAction($event)"
        />
      </div>

      <!-- Main content -->
      <form slot="content" [formGroup]="facade.form">
        <!-- Circuit breaker warning -->
        @if (facade.isCircuitOpen()) {
          <div class="webhook-edit-page__circuit-warning" role="alert">
            <span zagIcon="warning" class="webhook-edit-page__circuit-icon"></span>
            <p>{{ 'webhooks.circuitBreaker.warning' | translate }}</p>
          </div>
        }

        <zag-accordion>
          <!-- Configuration section -->
          <zag-accordion-item [title]="'webhooks.sections.configuration' | translate" icon="settings" [expanded]="true">
            <div class="webhook-edit-page__form">
              <zag-form-field [label]="'webhooks.fields.name' | translate" forId="wh-name" [required]="true">
                <zag-input
                  id="wh-name"
                  formControlName="name"
                  [disabled]="!canManage()"
                  [placeholder]="'webhooks.fields.namePlaceholder' | translate"
                />
              </zag-form-field>

              <zag-form-field
                [label]="'webhooks.fields.url' | translate"
                forId="wh-url"
                [required]="true"
                [error]="
                  facade.form.controls.url.touched && facade.form.controls.url.hasError('urlNotAllowed')
                    ? ('webhooks.validation.urlNotAllowed' | translate)
                    : ''
                "
              >
                <zag-input
                  id="wh-url"
                  formControlName="url"
                  [disabled]="!canManage()"
                  [placeholder]="'webhooks.fields.urlPlaceholder' | translate"
                />
              </zag-form-field>

              <!-- Event Types multi-select -->
              <div class="webhook-edit-page__field-group">
                <span class="webhook-edit-page__label" role="group">{{ 'webhooks.fields.eventTypes' | translate }}</span>
                <div class="webhook-edit-page__event-types">
                  @for (eventType of eventTypes; track eventType) {
                    <button
                      type="button"
                      class="webhook-edit-page__event-chip"
                      [class.webhook-edit-page__event-chip--selected]="facade.isEventTypeSelected(eventType)"
                      [disabled]="!canManage()"
                      [attr.aria-pressed]="facade.isEventTypeSelected(eventType)"
                      (click)="facade.toggleEventType(eventType)"
                    >
                      {{ 'notifications.eventTypes.' + eventType | translate }}
                    </button>
                  }
                </div>
                @if (facade.selectedEventTypes().size === 0) {
                  <span class="webhook-edit-page__field-hint webhook-edit-page__field-hint--error" role="alert">
                    {{ 'webhooks.validation.eventTypesRequired' | translate }}
                  </span>
                }
              </div>

              <!-- Active toggle -->
              <div class="webhook-edit-page__field-group">
                <label class="webhook-edit-page__label" [for]="'wh-active'">{{ 'webhooks.fields.isActive' | translate }}</label>
                <label class="webhook-edit-page__toggle-label">
                  <input
                    id="wh-active"
                    type="checkbox"
                    formControlName="isActive"
                    [disabled]="!canManage()"
                    class="webhook-edit-page__toggle-input"
                  />
                  <span class="webhook-edit-page__toggle-text">
                    {{ (facade.form.controls.isActive.value ? 'webhooks.status.active' : 'webhooks.status.inactive') | translate }}
                  </span>
                </label>
              </div>
            </div>
          </zag-accordion-item>

          <!-- Custom Headers section -->
          <zag-accordion-item
            [title]="'webhooks.sections.customHeaders' | translate"
            icon="http"
            [badge]="facade.customHeaderCount()"
            [expanded]="false"
          >
            <div formArrayName="customHeaders">
              @for (headerGroup of facade.customHeaderControls; track $index) {
                <div class="assignment-row" [formGroup]="headerGroup">
                  <div class="webhook-edit-page__header-input-group">
                    <input
                      class="webhook-edit-page__header-bare-input"
                      [formControl]="headerGroup.controls['key']"
                      [disabled]="!canManage()"
                      [placeholder]="'webhooks.fields.headerKey' | translate"
                      [attr.aria-label]="'webhooks.fields.headerKey' | translate"
                      (input)="facade.ensurePlaceholderRow()"
                    />
                    <span class="webhook-edit-page__header-colon" aria-hidden="true">:</span>
                    <input
                      class="webhook-edit-page__header-bare-input"
                      [formControl]="headerGroup.controls['value']"
                      [disabled]="!canManage()"
                      [placeholder]="'webhooks.fields.headerValue' | translate"
                      [attr.aria-label]="'webhooks.fields.headerValue' | translate"
                    />
                  </div>
                  @if (headerGroup.controls['key'].value && canManage()) {
                    <button
                      type="button"
                      zagButton
                      [attr.aria-label]="'common.remove' | translate"
                      (click)="facade.removeCustomHeader($index)"
                    >
                      <span zagIcon="delete"></span>
                    </button>
                  } @else {
                    <div class="assignment-row__spacer"></div>
                  }
                </div>
              }
            </div>
          </zag-accordion-item>

          <!-- Delivery Logs section -->
          @if (!facade.isNew() && canReadDeliveries()) {
            <zag-accordion-item [title]="'webhooks.deliveries.title' | translate" icon="history" [expanded]="false">
              <div class="webhook-edit-page__delivery-logs-placeholder">
                <span zagIcon="history" class="webhook-edit-page__delivery-logs-icon"></span>
                <p>{{ 'webhooks.deliveries.viewLogsHint' | translate }}</p>
              </div>
            </zag-accordion-item>
          }
        </zag-accordion>
      </form>
    </zag-entity-detail-layout>
  }
</div>
